import React, { useState, useEffect, useRef, useCallback } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Eye, ListFilter, Copy, Mic, MicOff,
  User, AlertTriangle, Zap, History, ClipboardCheck, Activity, Pill, FlaskConical, Calendar, ArrowLeft, CheckCircle, Menu, Clock,
  Pencil, X, FileText
} from 'lucide-react';
import PrescriptionViewer from './PrescriptionViewer';
import './CreatePrescription.css';
import { safeQuery } from '../utils/dbUtils';

const DOSAGE_OPTIONS = ['1 Tab', '1/2 Tab', '5 ml', '10 ml', '1 Drop', '1 Puff'];
const FREQ_OPTIONS = ['1-0-0', '1-0-1', '1-1-1', '0-0-1', 'SOS'];
const DURATION_OPTIONS = ['3 Days', '5 Days', '1 Week', '2 Weeks', '1 Month'];
const INSTRUCTION_OPTIONS = ['After Food', 'Before Food', 'Empty Stomach', 'With Warm Water'];


function CreatePrescription({
  doctorInfo,
  onComplete,
  // ... rest of props remain same
  preSelectedPatient,
  editPrescriptionData,
  hasPharmacy = true,
  hasLab = true,
  onGoToSearch,
  onRefreshProfile,
  onToggleMenu
}) {
  const isReceptionVitals = !!editPrescriptionData?.checked_in_by;

  // Helper for lazy initialization
  const getInitialDraft = () => {
    if (editPrescriptionData) return null;
    try {
      const stash = localStorage.getItem('klinik_rx_stash');
      return stash ? JSON.parse(stash) : null;
    } catch (e) { return null; }
  };

  const initialDraft = getInitialDraft();

  // Step 1: Core Form State (Lazy Initialized from disk)
  const [step, setStep] = useState(() => {
    if (initialDraft && initialDraft.step) return initialDraft.step;
    return (preSelectedPatient || editPrescriptionData) ? 3 : 1;
  });

  const [phoneNumber, setPhoneNumber] = useState(() => {
    if (initialDraft && initialDraft.phoneNumber) return initialDraft.phoneNumber;
    return preSelectedPatient?.phone || '';
  });

  const [existingPatient, setExistingPatient] = useState(() => {
    if (initialDraft && initialDraft.existingPatient) return initialDraft.existingPatient;
    return preSelectedPatient || null;
  });

  const [patientForm, setPatientForm] = useState(() => {
    if (initialDraft && initialDraft.patientForm) return initialDraft.patientForm;
    return {
      name: '', age: '', gender: 'male', weight: '', blood_group: '',
      bp_systolic: '', bp_diastolic: '', temperature: '', symptoms: '',
      spo2: '', pulse: ''
    };
  });

  const [diagnosis, setDiagnosis] = useState(() => initialDraft?.diagnosis || '');
  const [medicines, setMedicines] = useState(() => initialDraft?.medicines || [{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  const [tests, setTests] = useState(() => initialDraft?.tests || []);
  const [advice, setAdvice] = useState(() => initialDraft?.advice || '');
  const [followUpDate, setFollowUpDate] = useState(() => initialDraft?.followUpDate || '');
  const [pastMedicalHistory, setPastMedicalHistory] = useState(() => initialDraft?.pastMedicalHistory || '');
  const [lmp, setLmp] = useState(() => initialDraft?.lmp || '');
  const [isEditingPMH, setIsEditingPMH] = useState(false);

  // Pre-Consultation Lab Results (from check-in)
  const [preConsultationTests, setPreConsultationTests] = useState([]);
  const [preConsultationLabStatus, setPreConsultationLabStatus] = useState('');
  const [preConsultationLabResults, setPreConsultationLabResults] = useState(null);
  // Step 2: UI & Search States
  const [loading, setLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [recentPrescriptions, setRecentPrescriptions] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPrescriptionId = searchParams.get('prescription_id');
  
  const [activeMedIndex, setActiveMedIndex] = useState(null);
  const [medSuggestions, setMedSuggestions] = useState([]);
  const [isSearchingMed, setIsSearchingMed] = useState(false);
  const [activeChipField, setActiveChipField] = useState({ index: null, field: null });

  // Lab & Tests Logic
  const [testInput, setTestInput] = useState('');
  const [testSuggestions, setTestSuggestions] = useState([]);
  const [isSearchingTest, setIsSearchingTest] = useState(false);
  const [isTestInputFocused, setIsTestInputFocused] = useState(false);

  // UI Expansion & Navigation
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(true);
  const [isVitalsExpanded, setIsVitalsExpanded] = useState(true);
  const [isTestsExpanded, setIsTestsExpanded] = useState(true);
  const [viewingHistoryRx, setViewingHistoryRx] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  
  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState('section-clinical');

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const sections = ['section-clinical', 'section-medications', 'section-investigations', 'section-plan'];
      let currentSection = sections[0];
      let minDistance = Infinity;

      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Distance from the top of the container scroll viewport (approx. offset by sticky header height)
          const distance = Math.abs(rect.top - 140);
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = sectionId;
          }
        }
      }
      setActiveSection(currentSection);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [step]);

  const scrollToSection = (sectionId) => {
    const container = containerRef.current;
    const el = document.getElementById(sectionId);
    if (container && el) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const relativeTop = elRect.top - containerRect.top + container.scrollTop;
      // Offset by the sticky header height plus padding (110px total)
      const targetScrollTop = relativeTop - 110;
      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }
  };

  // Presets & Templates
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activePresetName, setActivePresetName] = useState(null);
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [showPresetInput, setShowPresetInput] = useState(false);
  const [presetNameInput, setPresetNameInput] = useState('');
  const [showRepeatConfirm, setShowRepeatConfirm] = useState(false);

  // Step 3: Voice & Hardware Logic
  const [listeningField, setListeningField] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);

  const recognitionRef = useRef(null);
  const activeFieldRef = useRef(null);
  const nextFieldRef = useRef(null);
  const isActuallyListeningRef = useRef(false);
  const processedResultIndexesRef = useRef(new Set());

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-IN';

      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            // Prevent double processing in strict mode by tracking unique result indexes
            if (!processedResultIndexesRef.current.has(i)) {
              processedResultIndexesRef.current.add(i);
              handleVoiceInput(event.results[i][0].transcript);
            }
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setInterimTranscript(interim);
      };

      rec.onstart = () => {
        isActuallyListeningRef.current = true;
        setListeningField(activeFieldRef.current);
        setInterimTranscript('');
        processedResultIndexesRef.current.clear();
      };

      rec.onend = () => {
        isActuallyListeningRef.current = false;
        setInterimTranscript('');

        // Handle queued switch safely
        if (nextFieldRef.current) {
          const field = nextFieldRef.current;
          nextFieldRef.current = null;
          activeFieldRef.current = field;
          setListeningField(field);
          try {
            rec.start();
          } catch (e) {
            console.error("Auto-start failed", e);
            activeFieldRef.current = null;
            setListeningField(null);
          }
        } else {
          activeFieldRef.current = null;
          setListeningField(null);
        }
      };

      rec.onerror = (event) => {
        if (event.error === 'aborted') return;
        console.error('Speech recognition error:', event.error);
        isActuallyListeningRef.current = false;
        activeFieldRef.current = null;
        setListeningField(null);
        setInterimTranscript('');
        if (event.error === 'not-allowed') toast.error('Microphone access denied');
      };

    recognitionRef.current = rec;
    setIsSpeechSupported(true);
  }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);




  // B. Atomic Save Logic
  const saveDraft = useCallback(() => {
    if (editPrescriptionData) return;
    
    const draft = {
      patientForm, diagnosis, medicines, tests, advice, 
      followUpDate, existingPatient, phoneNumber, step,
      pastMedicalHistory, lmp,
      lastSaved: Date.now()
    };
    
    const hasContent = diagnosis || advice || medicines.some(m => m.name) || tests.length > 0 || pastMedicalHistory || lmp;
    if (hasContent || existingPatient) {
      localStorage.setItem('klinik_rx_stash', JSON.stringify(draft));
    }
  }, [patientForm, diagnosis, medicines, tests, advice, followUpDate, existingPatient, phoneNumber, step, pastMedicalHistory, lmp, editPrescriptionData]);

  // C. Persistence Hook - Save changes to localStorage (Throttled/Reactive)
  useEffect(() => {
    saveDraft();
  }, [saveDraft]);

  // D. Terminal Recovery Listener - Synchronous Flush
  useEffect(() => {
    const handlePreReload = () => {
      console.log('[Atomic Save] Terminal recovery detected. Flushing to disk...');
      saveDraft();
    };
    window.addEventListener('klinik-pre-reload-save', handlePreReload);
    return () => window.removeEventListener('klinik-pre-reload-save', handlePreReload);
  }, [saveDraft]);





  function handleVoiceInput(text) {
    const cleanText = text.trim();
    if (!cleanText) return;

    // Use currentField tracking from the stable activeFieldRef to ensure accuracy
    const field = activeFieldRef.current;
    if (!field) return;

    // Auto capitalize first letter and add period
    const formattedText = cleanText.charAt(0).toUpperCase() + cleanText.slice(1) + (cleanText.endsWith('.') ? '' : '.');

    if (field === 'symptoms') {
      setPatientForm(prev => ({
        ...prev,
        symptoms: prev.symptoms ? `${prev.symptoms.trim()} ${formattedText}` : formattedText
      }));
    } else if (field === 'diagnosis') {
      setDiagnosis(prev => prev ? `${prev.trim()} ${formattedText}` : formattedText);
    } else if (field === 'advice') {
      setAdvice(prev => prev ? `${prev.trim()} ${formattedText}` : formattedText);
    } else if (field === 'pastMedicalHistory') {
      setPastMedicalHistory(prev => prev ? `${prev.trim()} ${formattedText}` : formattedText);
    }
  }

  function toggleListening(fieldName) {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported');
      return;
    }

    if (activeFieldRef.current === fieldName) {
      // Toggle OFF: Immediately update UI to show it's stopped
      activeFieldRef.current = null;
      setListeningField(null);

      try {
        // Use abort for immediate stop without waiting for final processing 
        // to make the button feel instantly responsive
        recognitionRef.current.stop();
      } catch (err) {
        console.error("Stop failed:", err);
      }
    } else {
      // Toggle ON or Switch fields
      if (isActuallyListeningRef.current) {
        // Queue the next field
        nextFieldRef.current = fieldName;
        // Optimistically update UI
        setListeningField(fieldName);
        try {
          recognitionRef.current.stop();
        } catch (err) { }
      } else {
        // Start fresh
        activeFieldRef.current = fieldName;
        setListeningField(fieldName);
        try {
          recognitionRef.current.start();
        } catch (err) {
          console.error('Manual start failed:', err);
          activeFieldRef.current = null;
          setListeningField(null);
        }
      }
    }
  }

  useEffect(() => {
    if (preSelectedPatient) loadPatientData(preSelectedPatient);
  }, [preSelectedPatient]);

  useEffect(() => {
    if (editPrescriptionData) {
      loadEditData(editPrescriptionData);
    }
  }, [editPrescriptionData]);

  useEffect(() => {
    if (step === 3 && !editPrescriptionData && !followUpDate && doctorInfo?.default_followup_days && doctorInfo.default_followup_days > 0) {
      setFollowUpDuration(doctorInfo.default_followup_days);
    }
  }, [step, doctorInfo, editPrescriptionData, followUpDate]);

  function setFollowUpDuration(days) {
    const date = new Date();
    date.setDate(date.getDate() + parseInt(days));
    setFollowUpDate(date.toISOString().split('T')[0]);
  }

  useEffect(() => {
    const searchSupabaseMedicines = async () => {
      if (activeMedIndex === null || !medicines[activeMedIndex]) {
        setMedSuggestions([]);
        setIsSearchingMed(false);
        return;
      }

      const query = medicines[activeMedIndex]?.name || '';
      const hasFavorites = doctorInfo?.favorite_medicines?.length > 0;

      if (!query || query.length < 1) {
        if (hasFavorites) {
          // Instantly show favorites on empty focus
          const favs = doctorInfo.favorite_medicines.map(f => ({ ...f, isFavorite: true }));
          setMedSuggestions(favs);
        } else {
          setMedSuggestions([]);
        }
        setIsSearchingMed(false);
        return;
      }

      setIsSearchingMed(true);
      try {
        const { data, error } = await supabase
          .rpc('search_medicines_fuzzy', { search_query: query, max_results: 10 });

        if (error) throw error;

        let results = data || [];

        if (hasFavorites) {
          const favNames = doctorInfo.favorite_medicines.map(f => f.name.toLowerCase());
          results = results.map(r => ({
            ...r,
            isFavorite: favNames.includes(r.name.toLowerCase())
          }));
          // Sort boolean: favorites float to top of the exact matches
          results.sort((a, b) => (b.isFavorite === true ? 1 : 0) - (a.isFavorite === true ? 1 : 0));
        }

        setMedSuggestions(results);
      } catch (err) {
        console.error('Error fetching medicine suggestions:', err);
      } finally {
        setIsSearchingMed(false);
      }
    };

    const debounceTimer = setTimeout(searchSupabaseMedicines, 300);
    return () => clearTimeout(debounceTimer);
  }, [medicines, activeMedIndex]);

  useEffect(() => {
    const searchSupabaseTests = async () => {
      if (!isTestInputFocused || !testInput || testInput.length < 1) {
        setTestSuggestions([]);
        setIsSearchingTest(false);
        return;
      }

      setIsSearchingTest(true);
      try {
        const { data, error } = await supabase
          .rpc('search_lab_tests_fuzzy', { search_query: testInput, max_results: 10 });

        if (error) throw error;
        setTestSuggestions(data || []);
      } catch (err) {
        console.error('Error fetching test suggestions:', err);
      } finally {
        setIsSearchingTest(false);
      }
    };

    const debounceTimer = setTimeout(searchSupabaseTests, 300);
    return () => clearTimeout(debounceTimer);
  }, [testInput, isTestInputFocused]);

  async function loadEditData(rx) {
    setLoading(true);
    try {
      // Need to fetch patient data as well since it might not be fully present in the prescription object
      let patientData = rx.patients;
      if (!patientData) {
        const { data, error } = await supabase
          .from('patients')
          .select('*')
          .eq('id', rx.patient_id)
          .single();
        if (error) throw error;
        patientData = data;
      }

      const bpRaw = rx.vitals?.bp || rx.vitals?.blood_pressure || '';
      const bpParts = bpRaw.includes('/') ? bpRaw.split('/') : ['', ''];
      setExistingPatient(patientData);
      setPhoneNumber(rx.patient_phone || patientData.phone);
      setPatientForm({
        name: rx.patient_name || patientData.name || '',
        age: rx.patient_age || patientData.age || '',
        gender: rx.patient_gender || patientData.gender || 'male',
        weight: rx.vitals?.weight || patientData.weight || '',
        blood_group: patientData.blood_group || '',
        bp_systolic: bpParts[0] || '',
        bp_diastolic: bpParts[1] || '',
        temperature: rx.vitals?.temperature || '',
        symptoms: rx.symptoms || '',
        spo2: rx.vitals?.spo2 || '',
        pulse: rx.vitals?.pulse || ''
      });
      setDiagnosis(rx.diagnosis || '');
      if (rx.medicines && rx.medicines.length > 0) setMedicines(rx.medicines);
      if (rx.tests && rx.tests.length > 0) setTests(rx.tests);
      if (rx.advice) setAdvice(rx.advice);
      if (rx.follow_up_date) setFollowUpDate(rx.follow_up_date);
      setPastMedicalHistory(rx.past_medical_history || patientData.conditions || '');
      setLmp(rx.lmp || '');

      // Also fetch recent prescriptions
      const { data: recentData } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('patient_id', patientData.id)
        .neq('id', rx.id)
        .order('created_at', { ascending: false });
      setRecentPrescriptions(recentData || []);

      setStep(3);
    } catch (err) {
      console.error('Error loading edit data:', err);
      toast.error('Failed to load prescription data for editing.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function loadDraftPrescription() {
      if (!currentPrescriptionId) return;
      try {
        setLoading(true);
        const { data: rx, error } = await supabase
          .from('prescriptions')
          .select('*, patients(*)')
          .eq('id', currentPrescriptionId)
          .single();
        if (error) throw error;

        if (rx) {
          const bpRaw = rx.vitals?.bp || rx.vitals?.blood_pressure || '';
          const bpParts = bpRaw.includes('/') ? bpRaw.split('/') : ['', ''];
          setExistingPatient(rx.patients);
          setPhoneNumber(rx.patients.phone);
          setPatientForm({
            name: rx.patients.name || '',
            age: rx.patients.age || '',
            gender: rx.patients.gender || 'male',
            weight: rx.patients.weight || '',
            blood_group: rx.patients.blood_group || '',
            bp_systolic: bpParts[0] || '',
            bp_diastolic: bpParts[1] || '',
            temperature: rx.vitals?.temperature || '',
            symptoms: rx.symptoms || ''
          });
          setDiagnosis(rx.diagnosis || '');
          if (rx.medicines && rx.medicines.length > 0) setMedicines(rx.medicines);
          if (rx.tests && rx.tests.length > 0) {
            setPreConsultationTests(rx.tests);
            setPreConsultationLabStatus(rx.lab_status || '');
            if (rx.lab_results) {
               try {
                 setPreConsultationLabResults(typeof rx.lab_results === 'string' ? JSON.parse(rx.lab_results) : rx.lab_results);
               } catch (e) { console.error('Failed to parse lab results', e); }
            }
          }
          if (rx.advice) setAdvice(rx.advice);
          if (rx.follow_up_date) setFollowUpDate(rx.follow_up_date);
          setPastMedicalHistory(rx.past_medical_history || rx.patients.conditions || '');
          setStep(3);
        }
      } catch (err) {
        console.error('Error loading draft prescription:', err);
      } finally {
        setLoading(false);
      }
    }
    // Only load from DB if we have an ID but the patient form is empty 
    // (which happens specifically when the page is refreshed)
    if (currentPrescriptionId && !existingPatient) {
      loadDraftPrescription();
    }
  }, [currentPrescriptionId, existingPatient]);

  async function loadPatientData(patient) {
    setExistingPatient(patient);
    setPhoneNumber(patient.phone);
    setPatientForm({
      name: patient.name || '',
      age: patient.age || '',
      gender: patient.gender || 'male',
      weight: patient.weight || '',
      blood_group: patient.blood_group || '',
      bp_systolic: '',
      bp_diastolic: '',
      temperature: '',
      symptoms: ''
    });
    setPastMedicalHistory(patient.conditions || '');

    try {
      const { data } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('patient_id', patient.id)
        .order('created_at', { ascending: false });
      setRecentPrescriptions(data || []);
    } catch (error) {
      console.error('Error fetching recent prescriptions:', error);
    }

    setStep(3);
  }

  async function handleSearchPatient(e) {
    e.preventDefault();
    if (phoneNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('phone', phoneNumber)
        .eq('clinic_id', doctorInfo.clinic_id)
        .limit(1);
      if (error) throw error;

      if (data && data.length > 0) {
        await loadPatientData(data[0]);
      } else {
        setExistingPatient(null);
        setRecentPrescriptions([]);
        setStep(2);
      }
    } catch (error) {
      toast.error('Error searching patient: ' + error.message);
    }
    setLoading(false);
  }

  async function handleSavePatient(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patients')
        .insert({
          name: patientForm.name,
          phone: phoneNumber,
          age: parseInt(patientForm.age),
          gender: patientForm.gender,
          conditions: pastMedicalHistory || null,
          clinic_id: doctorInfo.clinic_id,
          created_by: doctorInfo.id
        })
        .select()
        .single();
      if (error) throw error;

      setExistingPatient(data);
      setRecentPrescriptions([]);
      setStep(3);
    } catch (error) {
      toast.error('Error saving patient: ' + error.message);
    }
    setLoading(false);
  }

  function addMedicine() {
    setMedicines([...medicines, { name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  }

  function removeMedicine(index) {
    setMedicines(medicines.filter((_, i) => i !== index));
  }

  function updateMedicine(index, field, value) {
    const updated = [...medicines];
    updated[index][field] = value;
    setMedicines(updated);
    if (field === 'name') {
      setActiveMedIndex(index);
    }
  }

  function selectMedicineSuggestion(index, medParams) {
    const updated = [...medicines];
    updated[index].name = medParams.name;
    setMedicines(updated);
    setActiveMedIndex(null);
    setMedSuggestions([]);
  }

  function addTest() {
    if (testInput.trim()) {
      setTests([...tests, testInput.trim()]);
      setTestInput('');
      setTestSuggestions([]);
      setIsSearchingTest(false);
    }
  }

  function selectTestSuggestion(sugg) {
    setTests(prev => [...prev, sugg.name]);
    setTestInput('');
    setTestSuggestions([]);
    // We keep focus state true to allow adding multiple tests sequentially
  }

  function removeTest(index) {
    setTests(tests.filter((_, i) => i !== index));
  }

  async function handleSaveTemplate() {
    if (!diagnosis.trim()) { toast.error('Please enter a diagnosis for the template'); return; }
    const validMedicines = medicines.filter(m => m.name.trim());
    if (validMedicines.length === 0 && tests.length === 0) {
      toast.error('Template needs at least one medicine or test');
      return;
    }

    if (!showPresetInput) {
      setShowPresetInput(true);
      return;
    }

    if (!presetNameInput.trim()) {
      toast.error('Please enter a name for the preset');
      return;
    }

    const tplName = presetNameInput.trim();

    const newTemplate = {
      template_name: tplName,
      diagnosis: diagnosis.trim(),
      medicines: validMedicines,
      tests: tests,
      advice: advice.trim()
    };

    setSavingTemplate(true);
    try {
      const currentTemplates = doctorInfo?.prescription_templates || [];
      const updatedTemplates = [...currentTemplates, newTemplate];

      const { data, error } = await supabase
        .from('users')
        .update({ prescription_templates: updatedTemplates })
        .eq('id', doctorInfo.id)
        .select();

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Supabase blocked the update. Check row-level security (RLS) policies for UPDATE on the "users" table.');
      }

      toast.success(`Preset "${tplName}" saved successfully!`);
      setActivePresetName(tplName);
      setShowPresetInput(false);
      setPresetNameInput('');
      if (onRefreshProfile) onRefreshProfile();
    } catch (err) {
      toast.error('Error saving preset: ' + err.message);
    } finally {
      setSavingTemplate(false);
    }
  }

  function repeatPrescription(rx) {
    setDiagnosis(rx.diagnosis || '');
    if (rx.medicines && rx.medicines.length > 0) {
      setMedicines(JSON.parse(JSON.stringify(rx.medicines)));
    } else {
      setMedicines([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
    }
    if (rx.tests && rx.tests.length > 0) {
      setTests([...rx.tests]);
    } else {
      setTests([]);
    }
    setAdvice(rx.advice || '');
    setShowRepeatConfirm(false);
    toast.success(`Copied details from prescription dated ${new Date(rx.created_at).toLocaleDateString()}`);
  }

  function applyTemplate(tpl) {
    setDiagnosis(tpl.diagnosis || '');
    if (tpl.medicines && tpl.medicines.length > 0) {
      setMedicines(tpl.medicines);
    } else {
      setMedicines([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
    }
    setTests(tpl.tests || []);
    setAdvice(tpl.advice || '');
    setActivePresetName(tpl.template_name);
    toast.success(`Loaded "${tpl.template_name}" template`);
  }

  async function handleSavePrescription(action) {
    if (!patientForm.symptoms.trim()) { toast.error('Please enter symptoms / chief complaints'); return; }
    if (!diagnosis.trim()) { toast.error('Please enter a diagnosis'); return; }

    const validMedicines = medicines.filter(m => m.name.trim());
    if (validMedicines.length === 0 && action !== 'lab') {
      toast.error('Please add at least one medicine');
      return;
    }

    setLoading(true);
    try {
      let status = 'draft';
      let sentToPharmacy = false;
      let sentToLab = false;

      // Determine routing
      if (action === 'pharmacy') { sentToPharmacy = true; }
      else if (action === 'lab') { sentToLab = true; }
      else if (action === 'done') {
        sentToPharmacy = validMedicines.length > 0;
        sentToLab = tests.length > 0;
      }

      // Determine overall status
      if (hasPharmacy && hasLab && sentToPharmacy && sentToLab) {
        status = 'sent_to_both';
      } else if (hasPharmacy && sentToPharmacy) {
        status = 'sent_to_pharmacy';
      } else if (hasLab && sentToLab) {
        status = 'sent_to_lab';
      } else {
        status = 'completed';
      }

      const isFemale = (patientForm?.gender || existingPatient?.gender || '').toLowerCase() === 'female';

      const prescriptionData = {
        patient_id: existingPatient.id,
        patient_name: patientForm.name,
        patient_age: parseInt(patientForm.age),
        patient_gender: patientForm.gender,
        patient_phone: phoneNumber,
        doctor_id: doctorInfo.id,
        doctor_name: doctorInfo.name,
        doctor_qualification: doctorInfo.qualification,
        doctor_specialization: doctorInfo.specialization,
        clinic_id: doctorInfo.clinic_id,
        diagnosis,
        medicines: validMedicines,
        tests: [...preConsultationTests, ...tests],
        advice,
        follow_up_date: followUpDate || null,
        vitals: {
          bp: (patientForm.bp_systolic && patientForm.bp_diastolic) ? `${patientForm.bp_systolic}/${patientForm.bp_diastolic}` : null,
          blood_pressure: (patientForm.bp_systolic && patientForm.bp_diastolic) ? `${patientForm.bp_systolic}/${patientForm.bp_diastolic}` : null,
          temperature: patientForm.temperature || null,
          spo2: patientForm.spo2 ? parseInt(patientForm.spo2) : null,
          pulse: patientForm.pulse ? parseInt(patientForm.pulse) : null
        },
        symptoms: patientForm.symptoms,
        past_medical_history: pastMedicalHistory || null,
        lmp: isFemale ? (lmp || null) : null,
        status,
        pharmacy_status: sentToPharmacy ? 'pending' : 'not_applicable',
        lab_status: sentToLab ? 'pending' : (preConsultationTests.length > 0 ? preConsultationLabStatus : 'not_applicable'),
        sent_to_pharmacy: sentToPharmacy,
        sent_to_lab: sentToLab || preConsultationTests.length > 0
      };

      let newRx;
      let error;

      let result;

      if (editPrescriptionData) {
        prescriptionData.is_edited = true;
        prescriptionData.last_edited_at = new Date().toISOString();
        result = await safeQuery(
          supabase
            .from('prescriptions')
            .update(prescriptionData)
            .eq('id', editPrescriptionData.id)
            .select()
            .single(),
          { label: 'Update Prescription' }
        );
      } else {
        result = await safeQuery(
          supabase
            .from('prescriptions')
            .insert(prescriptionData)
            .select()
            .single(),
          { label: 'Save Prescription' }
        );
      }

      newRx = result.data;
      error = result.error;

      if (error) throw error;

      // Successfully saved - clear the draft
      localStorage.removeItem('klinik_rx_stash');

      // Update patient's last visit

      await safeQuery(
        supabase
          .from('patients')
          .update({
            last_visit: new Date().toISOString(),
            weight: patientForm.weight ? parseFloat(patientForm.weight) : null,
            blood_group: patientForm.blood_group,
            conditions: pastMedicalHistory || null
          })
          .eq('id', existingPatient.id),
        { label: 'Update Patient Visit', showToast: false }
      );

      if (action === 'done') {
        toast.success('Prescription completed successfully!');
        if (onComplete) onComplete();
        resetForm();
      } else {
        const hasBothPending = (action === 'pharmacy' && tests.length > 0) || (action === 'lab' && validMedicines.length > 0);
        if (hasBothPending) {
          toast.success(`Prescription sent to ${action}! You can also send it to the other department.`);
          setSearchParams({ prescription_id: newRx.id });
        } else {
          toast.success(`Prescription sent to ${action === 'pharmacy' ? 'pharmacy' : 'lab'} successfully!`);
          if (onComplete) onComplete();
          resetForm();
        }
      }
    } catch (error) {
      toast.error('Error saving prescription: ' + error.message);
    }
    setLoading(false);
  }

  async function handleSendToAdditionalDept() {
    if (!currentPrescriptionId) return;
    setLoading(true);
    try {
      const { data: existing, error: fetchErr } = await supabase
        .from('prescriptions')
        .select('sent_to_pharmacy, sent_to_lab')
        .eq('id', currentPrescriptionId)
        .single();
      if (fetchErr) throw fetchErr;

      const updates = { updated_at: new Date().toISOString() };
      const validMedicines = medicines.filter(m => m.name.trim());

      let newPharmacy = existing.sent_to_pharmacy;
      let newLab = existing.sent_to_lab;

      if (hasPharmacy && !existing.sent_to_pharmacy && validMedicines.length > 0) {
        updates.sent_to_pharmacy = true;
        updates.pharmacy_status = 'pending';
        newPharmacy = true;
      } else if (hasLab && !existing.sent_to_lab && tests.length > 0) {
        updates.sent_to_lab = true;
        updates.lab_status = 'pending';
        newLab = true;
      }

      if (hasPharmacy && hasLab && newPharmacy && newLab) {
        updates.status = 'sent_to_both';
      } else if (hasPharmacy && newPharmacy) {
        updates.status = 'sent_to_pharmacy';
      } else if (hasLab && newLab) {
        updates.status = 'sent_to_lab';
      }

      const { error } = await supabase
        .from('prescriptions')
        .update(updates)
        .eq('id', currentPrescriptionId);
      if (error) throw error;

      toast.success('Prescription updated successfully!');
      if (onComplete) onComplete();
      resetForm();
    } catch (error) {
      toast.error('Error updating: ' + error.message);
    }
    setLoading(false);
  }

  function resetForm() {
    setStep(1); setPhoneNumber(''); setExistingPatient(null); setRecentPrescriptions([]);
    setPatientForm({ name: '', age: '', gender: 'male', weight: '', blood_group: '', bp_systolic: '', bp_diastolic: '', temperature: '', symptoms: '', spo2: '', pulse: '' });
    setDiagnosis(''); setMedicines([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
    setTests([]); setAdvice(''); setFollowUpDate('');
    setPastMedicalHistory(''); setLmp('');
    setSearchParams({});
    setActivePresetName(null);
    setShowPresetInput(false);
    setPresetNameInput('');
  }

  function handleBackClick() {
    if (step === 3) {
      setShowLeaveModal(true);
    } else {
      resetForm();
    }
  }

  return (
    <div className="prescription-container" ref={containerRef}>
      {step === 1 && (
        <div className="prescription-card">
          <h2>Enter Patient Phone Number</h2>
          <p className="subtitle">Search for existing patient or create new</p>
          <form onSubmit={handleSearchPatient}>
            <div className="phone-input-group">
              <span className="phone-prefix">+91</span>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="9876543210"
                maxLength="10"
                required
                autoFocus
              />
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Searching...' : 'Continue'}
            </button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div className="prescription-card">
          <div className="card-header">
            <h2>New Patient - {phoneNumber}</h2>
            <button onClick={resetForm} className="btn-back">← Back</button>
          </div>
          <form onSubmit={handleSavePatient} className="patient-form">
            <div className="form-row">
              <div className="form-group">
                <label>Patient Name *</label>
                <input type="text" value={patientForm.name}
                  onChange={(e) => setPatientForm({ ...patientForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Age *</label>
                <input type="number" value={patientForm.age}
                  onChange={(e) => setPatientForm({ ...patientForm, age: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Gender *</label>
                <select value={patientForm.gender}
                  onChange={(e) => setPatientForm({ ...patientForm, gender: e.target.value })}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Past Medical History</label>
              <textarea value={pastMedicalHistory}
                onChange={(e) => setPastMedicalHistory(e.target.value)}
                placeholder="e.g., Diabetes, Hypertension, Previous surgeries"
                rows="2" />
            </div>
            <div className="form-group">
              <label>Symptoms / Chief Complaints *</label>
              <textarea value={patientForm.symptoms}
                onChange={(e) => setPatientForm({ ...patientForm, symptoms: e.target.value })}
                required rows="3" />
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Continue to Prescription'}
            </button>
          </form>
        </div>
      )}      {step === 3 && (
        <div className="prescription-form-container-v2">
          <div className="prescription-container-v2 full-width-container">
            <div className="prescription-top-spacer no-print" style={{ height: '1.5rem', flexShrink: 0 }} />

            {/* PREMIUM STICKY HEADER */}
            <header className="premium-sticky-header no-print">
              <div className="premium-header-top">

                {/* Left Side: Avatar + Details in a beautifully styled group */}
                <div className="premium-header-patient-info">
                  <button onClick={handleBackClick} className="premium-back-icon-btn" title="Back to Search">
                    <ArrowLeft size={16} strokeWidth={2.5} />
                  </button>
                  <div className="patient-avatar-circle">
                    {patientForm.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="patient-details-stack">
                    <div className="patient-name-row">
                      <h2 className="header-patient-name">{patientForm.name}</h2>
                      {recentPrescriptions.length > 0 && <span className="premium-badge badge-returning">Returning</span>}
                    </div>
                    <div className="patient-meta-row">
                      <span className="patient-meta-item">{patientForm.gender}</span>
                      <span className="meta-bullet">•</span>
                      <span className="patient-meta-item">{patientForm.age} yrs</span>
                      <span className="meta-bullet">•</span>
                      <span className="patient-meta-item">{phoneNumber}</span>
                      {existingPatient?.last_visit && (
                        <>
                          <span className="meta-bullet">•</span>
                          <span className="patient-meta-item">Last visit: {new Date(existingPatient.last_visit).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Middle: Allergies */}
                <div className="premium-header-conditions">

                </div>

                {/* Right Side: Action Buttons Toolbar */}
                <div className="premium-header-actions">
                  <div className="premium-actions-toolbar">
                    {recentPrescriptions.length > 0 && !editPrescriptionData && (
                      <div className="toolbar-btn-container">
                        <button
                          className={`toolbar-btn btn-repeat ${showRepeatConfirm ? 'active-confirm' : ''}`}
                          onClick={() => setShowRepeatConfirm(!showRepeatConfirm)}
                          data-tooltip={`Repeat Rx (Last visit: ${new Date(recentPrescriptions[0].created_at).toLocaleDateString()})`}
                        >
                          <Copy size={16} /> <span>Repeat Rx</span>
                        </button>
                        
                        <AnimatePresence>
                          {showRepeatConfirm && (
                            <motion.div 
                              initial={{ opacity: 0, scale: 0.95, y: 10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: 10 }}
                              className="repeat-confirm-popover shadow-xl"
                            >
                              <p>Copy last prescription?</p>
                              <div className="popover-actions">
                                <button onClick={() => setShowRepeatConfirm(false)} className="popover-cancel-sm">Cancel</button>
                                <button onClick={() => repeatPrescription(recentPrescriptions[0])} className="popover-confirm-sm">Confirm</button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {doctorInfo?.prescription_templates?.length > 0 && !editPrescriptionData && (
                      <div className="toolbar-btn-container">
                        <button
                          className="toolbar-btn btn-presets"
                          onClick={() => setIsPresetOpen(!isPresetOpen)}
                          data-tooltip="Apply a pre-defined set of medications and clinical advice from your saved presets."
                        >
                          <ListFilter size={16} /> <span>Presets</span>
                        </button>
                        {isPresetOpen && (
                          <div className="presets-dropdown premium-dropdown shadow-lg">
                            {doctorInfo.prescription_templates.map((tpl, i) => (
                              <button
                                key={i}
                                className="preset-item"
                                onClick={() => {
                                  applyTemplate(tpl);
                                  setIsPresetOpen(false);
                                }}
                              >
                                {tpl.template_name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {recentPrescriptions.length > 0 && (
                      <div className="toolbar-btn-container">
                        <button
                          className="toolbar-btn btn-history-v2"
                          onClick={() => setIsHistoryCollapsed(!isHistoryCollapsed)}
                        >
                          <History size={16} /> <span>History ({recentPrescriptions.length})</span>
                          <ChevronDown size={14} className={!isHistoryCollapsed ? 'rotate-180' : ''} />
                        </button>
                        {!isHistoryCollapsed && (
                          <div className="history-dropdown-menu-v2 shadow-xl">
                            <div className="history-dropdown-header">
                              <h4>Past Visits</h4>
                            </div>
                            <div className="history-dropdown-body">
                              {recentPrescriptions.slice(0, 3).map(rx => {
                                const isExpanded = expandedHistoryId === rx.id;
                                return (
                                  <div
                                    key={rx.id}
                                    className={`history-item-compact ${isExpanded ? 'expanded' : ''}`}
                                    onClick={() => setExpandedHistoryId(isExpanded ? null : rx.id)}
                                  >
                                    <div className="history-compact-header">
                                      <div className="history-compact-main">
                                        <div className="history-date-compact">{new Date(rx.created_at).toLocaleDateString()}</div>
                                        <div className="history-diagnosis-preview">
                                          <strong>Dx:</strong> {rx.diagnosis}
                                        </div>
                                      </div>
                                      <div className="history-actions-compact">
                                        <button className="btn-icon-sm" title="Copy Rx" onClick={(e) => { e.stopPropagation(); repeatPrescription(rx); }}>
                                          <Copy size={14} />
                                        </button>
                                        <button className="btn-icon-sm" title="View PDF" onClick={(e) => { e.stopPropagation(); setViewingHistoryRx(rx); }}>
                                          <Eye size={14} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="history-compact-body">
                                      {rx.medicines && rx.medicines.length > 0 && (
                                        <div className="history-detail-row">
                                          <strong>Medicines:</strong>
                                          <ul className="history-mini-list">
                                            {rx.medicines.map((med, idx) => (
                                              <li key={idx}>{med.name}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="history-dropdown-footer">
                              <button
                                className="btn-view-all-history"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsHistoryCollapsed(true);
                                  if (onGoToSearch && phoneNumber) onGoToSearch(phoneNumber);
                                  else toast.info("Check 'Search Patient' tab for full history");
                                }}
                              >
                                View Full History Search
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </header>

            <div className="prescription-split-layout">
              <main className="rx-main premium-main-layout">

              {/* SECTION: Clinical Notes */}
              <div id="section-clinical" className="section-card section-card-clinical">
                <div
                  className="section-collapsible-header"
                  onClick={() => setIsVitalsExpanded(!isVitalsExpanded)}
                >
                  <div className="section-title">
                    <Activity size={20} /> Vitals & Notes
                  </div>
                  <ChevronDown
                    size={20}
                    className={`chevron-icon ${isVitalsExpanded ? 'open' : ''}`}
                  />
                </div>

                <AnimatePresence>
                  {isVitalsExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="collapsible-container-motion"
                    >
                                    <div className="form-row mt-4 vitals-compact-row">
                      <div className="form-group vitals-compact-group">
                          <label>BP (mmHg)</label>
                          <div className="bp-split-input vitals-compact-split">
                            <input type="number" value={patientForm.bp_systolic}
                              onChange={(e) => setPatientForm({ ...patientForm, bp_systolic: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                              placeholder="120" className="bp-input vitals-compact-input" min="0" max="300" />
                            <span className="bp-slash">/</span>
                            <input type="number" value={patientForm.bp_diastolic}
                              onChange={(e) => setPatientForm({ ...patientForm, bp_diastolic: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                              placeholder="80" className="bp-input vitals-compact-input" min="0" max="200" />
                          </div>
                        </div>
                        <div className="form-group vitals-compact-group">
                          <label>SpO2 (%)</label>
                          <input type="number" value={patientForm.spo2}
                            onChange={(e) => setPatientForm({ ...patientForm, spo2: e.target.value })}
                            placeholder="98" className="vitals-compact-input" />
                        </div>
                        <div className="form-group vitals-compact-group">
                          <label>Pulse (bpm)</label>
                          <input type="number" value={patientForm.pulse}
                            onChange={(e) => setPatientForm({ ...patientForm, pulse: e.target.value })}
                            placeholder="72" className="vitals-compact-input" />
                        </div>
                        <div className="form-group vitals-compact-group">
                          <label>Temp (°F)</label>
                          <input type="text" value={patientForm.temperature}
                            onChange={(e) => setPatientForm({ ...patientForm, temperature: e.target.value })}
                            placeholder="98.6" className="vitals-compact-input" />
                        </div>
                      </div>

                      {preConsultationTests.length > 0 && (
                        <div className="pre-consultation-labs-block mt-4" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem', display: 'block' }}>Pre-Consultation Labs</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {preConsultationTests.map((test, idx) => {
                               const isCompleted = ['completed', 'report_uploaded'].includes(preConsultationLabStatus) || preConsultationLabResults;
                               return (
                                 <div key={idx} style={{ 
                                   display: 'flex', alignItems: 'center', gap: '0.5rem', 
                                   background: isCompleted ? '#ecfdf5' : '#fffbeb',
                                   border: `1px solid ${isCompleted ? '#a7f3d0' : '#fde68a'}`,
                                   padding: '0.375rem 0.75rem', borderRadius: '999px', fontSize: '0.85rem', fontWeight: '500',
                                   color: isCompleted ? '#065f46' : '#92400e'
                                 }}>
                                    <span>{test}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.8, fontSize: '0.75rem', marginLeft: '0.25rem', paddingLeft: '0.5rem', borderLeft: `1px solid ${isCompleted ? '#a7f3d0' : '#fde68a'}` }}>
                                      {isCompleted ? <CheckCircle size={12} /> : <Clock size={12} />}
                                      {isCompleted ? 'Completed' : 'Pending'}
                                    </span>
                                 </div>
                               );
                            })}
                          </div>
                        </div>
                      )}

                      {((patientForm?.gender || existingPatient?.gender || '').toLowerCase() === 'female') && (
                        <div className="form-group form-group-rel mt-4">
                          <label>Last Menstrual Period (LMP)</label>
                          <input 
                            type="date" 
                            value={lmp}
                            onChange={(e) => setLmp(e.target.value)}
                            className="premium-input"
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                  <div className="form-group rel-mt-5">
                    <label>Symptoms / Chief Complaints *</label>
                    <div className="premium-input-wrapper">
                      <textarea 
                        value={patientForm.symptoms}
                        onChange={(e) => setPatientForm({ ...patientForm, symptoms: e.target.value })}
                        required rows="2" 
                        placeholder="Enter chief complaints..."
                      />
                      {isSpeechSupported && (
                        <button 
                          type="button" 
                          className={`mic-docked-btn ${listeningField === 'symptoms' ? 'active' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleListening('symptoms'); }}
                          data-tooltip={listeningField === 'symptoms' ? "Stop Recording" : "Speak to enter symptoms"}
                        >
                          {listeningField === 'symptoms' ? <div className="stop-square" /> : <Mic size={18} />}
                        </button>
                      )}
                      <AnimatePresence>
                        {listeningField === 'symptoms' && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="dictation-status-floating"
                          >
                            <span className="dot pulse"></span> Listening...
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Only show PMH in vitals if patient doesn't already have it saved */}
                  {!existingPatient?.conditions && (
                  <div className="form-group form-group-rel rel-mt-5">
                    <label>Past Medical History</label>
                    <div className="premium-input-wrapper">
                      <textarea 
                        value={pastMedicalHistory}
                        onChange={(e) => setPastMedicalHistory(e.target.value)}
                        rows="2" 
                        placeholder="Enter past medical history..."
                      />
                      {isSpeechSupported && (
                        <button 
                          type="button" 
                          className={`mic-docked-btn ${listeningField === 'pastMedicalHistory' ? 'active' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleListening('pastMedicalHistory'); }}
                          data-tooltip={listeningField === 'pastMedicalHistory' ? "Stop Recording" : "Speak to enter past medical history"}
                        >
                          {listeningField === 'pastMedicalHistory' ? <div className="stop-square" /> : <Mic size={18} />}
                        </button>
                      )}
                      <AnimatePresence>
                        {listeningField === 'pastMedicalHistory' && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="dictation-status-floating"
                          >
                            <span className="dot pulse"></span> Listening...
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                  )}



                  <div className="form-group form-group-rel rel-mt-5">
                    <label>Diagnosis *</label>
                    <div className="premium-input-wrapper">
                      <input 
                        type="text" 
                        value={diagnosis}
                        onChange={(e) => setDiagnosis(e.target.value)}
                        placeholder="Enter primary diagnosis..." required 
                      />
                      {isSpeechSupported && (
                        <button 
                          type="button" 
                          className={`mic-docked-btn ${listeningField === 'diagnosis' ? 'active' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleListening('diagnosis'); }}
                          data-tooltip={listeningField === 'diagnosis' ? "Stop Recording" : "Speak to enter diagnosis"}
                        >
                          {listeningField === 'diagnosis' ? <div className="stop-square" /> : <Mic size={18} />}
                        </button>
                      )}
                      <AnimatePresence>
                        {listeningField === 'diagnosis' && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="dictation-status-floating"
                          >
                            <span className="dot pulse"></span> Listening...
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
              </div>

              {/* SECTION: Medications */}
              <div id="section-medications" className="section-card section-card-medications">
                <div className="section-title">
                  <Pill size={20} /> Medications
                </div>
                {medicines.map((medicine, index) => (
                  <div key={index} className="medicine-row">
                    <div className="medicine-fields">
                      <div className="form-group autocomplete-wrapper">
                        <label>Medicine Name *</label>
                        <input type="text" value={medicine.name}
                          onChange={(e) => updateMedicine(index, 'name', e.target.value)}
                          onFocus={() => setActiveMedIndex(index)}
                          onBlur={() => setTimeout(() => setActiveMedIndex(null), 200)}
                          placeholder="Type medicine..."
                          autoComplete="off" />

                        {activeMedIndex === index && (isSearchingMed || medSuggestions.length > 0) && (
                          <div className="autocomplete-dropdown">
                            {isSearchingMed ? (
                              <div className="autocomplete-loading">Searching...</div>
                            ) : (
                              medSuggestions.map((sugg, i) => (
                                <div
                                  key={sugg.id || `fav-${i}`}
                                  className={`autocomplete-item ${sugg.isFavorite ? 'favorite-item' : ''}`}
                                  onMouseDown={(e) => { e.preventDefault(); selectMedicineSuggestion(index, sugg); }}
                                >
                                  <strong>
                                    {sugg.isFavorite && <span className="fav-star">★</span>}
                                    {sugg.name}
                                  </strong>
                                  {sugg.generic_name ? <div className="sugg-generic">{sugg.generic_name}</div> : (sugg.composition && <div className="sugg-generic">{sugg.composition}</div>)}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <div className="form-group form-group-relative">
                        <label>Dosage</label>
                        <input type="text" value={medicine.dosage}
                          onChange={(e) => updateMedicine(index, 'dosage', e.target.value)}
                          onFocus={() => setActiveChipField({ index, field: 'dosage' })}
                          onBlur={() => setTimeout(() => setActiveChipField(prev => prev.index === index && prev.field === 'dosage' ? { index: null, field: null } : prev), 200)}
                          placeholder="e.g. 1 tab" />
                        {activeChipField.index === index && activeChipField.field === 'dosage' && (
                          <div className="quick-chip-container popover" onMouseDown={(e) => e.preventDefault()}>
                            {DOSAGE_OPTIONS.map(opt => (
                              <button type="button" key={opt} 
                                className={`quick-chip ${medicine.dosage.toLowerCase() === opt.toLowerCase() ? 'active' : ''}`}
                                onMouseDown={(e) => { e.preventDefault(); updateMedicine(index, 'dosage', opt); }}
                              >{opt}</button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="form-group form-group-relative">
                        <label>Frequency</label>
                        <input type="text" value={medicine.frequency}
                          onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                          onFocus={() => setActiveChipField({ index, field: 'frequency' })}
                          onBlur={() => setTimeout(() => setActiveChipField(prev => prev.index === index && prev.field === 'frequency' ? { index: null, field: null } : prev), 200)}
                          placeholder="e.g. 1-0-1" />
                        {activeChipField.index === index && activeChipField.field === 'frequency' && (
                          <div className="quick-chip-container popover" onMouseDown={(e) => e.preventDefault()}>
                            {FREQ_OPTIONS.map(opt => (
                              <button type="button" key={opt} 
                                className={`quick-chip ${medicine.frequency.toLowerCase() === opt.toLowerCase() ? 'active' : ''}`}
                                onMouseDown={(e) => { e.preventDefault(); updateMedicine(index, 'frequency', opt); }}
                              >{opt}</button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="form-group form-group-relative">
                        <label>Duration</label>
                        <input type="text" value={medicine.duration}
                          onChange={(e) => updateMedicine(index, 'duration', e.target.value)}
                          onFocus={() => setActiveChipField({ index, field: 'duration' })}
                          onBlur={() => setTimeout(() => setActiveChipField(prev => prev.index === index && prev.field === 'duration' ? { index: null, field: null } : prev), 200)}
                          placeholder="e.g. 5 days" />
                        {activeChipField.index === index && activeChipField.field === 'duration' && (
                          <div className="quick-chip-container popover" onMouseDown={(e) => e.preventDefault()}>
                            {DURATION_OPTIONS.map(opt => (
                              <button type="button" key={opt} 
                                className={`quick-chip ${medicine.duration.toLowerCase() === opt.toLowerCase() ? 'active' : ''}`}
                                onMouseDown={(e) => { e.preventDefault(); updateMedicine(index, 'duration', opt); }}
                              >{opt}</button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="form-group form-group-relative">
                        <label>Instructions</label>
                        <input type="text" value={medicine.instructions}
                          onChange={(e) => updateMedicine(index, 'instructions', e.target.value)}
                          onFocus={() => setActiveChipField({ index, field: 'instructions' })}
                          onBlur={() => setTimeout(() => setActiveChipField(prev => prev.index === index && prev.field === 'instructions' ? { index: null, field: null } : prev), 200)}
                          placeholder="e.g. After Food" />
                        {activeChipField.index === index && activeChipField.field === 'instructions' && (
                          <div className="quick-chip-container popover" onMouseDown={(e) => e.preventDefault()}>
                            {INSTRUCTION_OPTIONS.map(opt => (
                              <button type="button" key={opt} 
                                className={`quick-chip ${medicine.instructions.toLowerCase() === opt.toLowerCase() ? 'active' : ''}`}
                                onMouseDown={(e) => { e.preventDefault(); updateMedicine(index, 'instructions', opt); }}
                              >{opt}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {medicines.length > 1 && (
                      <button type="button" onClick={() => removeMedicine(index)} className="btn-remove">×</button>
                    )}
                  </div>
                ))}
                <div className="add-medicine-bottom">
                  <button type="button" onClick={addMedicine} className="btn-add-secondary">+ Add Medicine</button>
                </div>
              </div>

              {/* SECTION: Investigations */}
              <div id="section-investigations" className="section-card section-card-investigations">
                <div
                  className="section-collapsible-header"
                  onClick={() => setIsTestsExpanded(!isTestsExpanded)}
                >
                  <div className="section-title">
                    <FlaskConical size={20} /> Lab Investigations
                  </div>
                  <ChevronDown
                    size={20}
                    className={`chevron-icon ${isTestsExpanded ? 'open' : ''}`}
                  />
                </div>

                {isTestsExpanded && (
                  <div className="mt-4 animate-slide-down">
                    <div className="test-input-group autocomplete-wrapper">
                      <input type="text" value={testInput}
                        onChange={(e) => setTestInput(e.target.value)}
                        onFocus={() => setIsTestInputFocused(true)}
                        onBlur={() => setTimeout(() => setIsTestInputFocused(false), 200)}
                        placeholder="Search for lab test..."
                        autoComplete="off"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTest())} />
                      <button type="button" onClick={addTest} className="btn-add-small">Add</button>

                      {isTestInputFocused && (isSearchingTest || testSuggestions.length > 0) && (
                        <div className="autocomplete-dropdown">
                          {isSearchingTest ? (
                            <div className="autocomplete-loading">Searching...</div>
                          ) : (
                            testSuggestions.map(sugg => (
                              <div key={sugg.id} className="autocomplete-item" onMouseDown={(e) => { e.preventDefault(); selectTestSuggestion(sugg); }}>
                                <strong>{sugg.name}</strong>
                                {sugg.category && <span className="sugg-type">{sugg.category}</span>}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {tests.length > 0 && (
                      <div className="tests-list">
                        {tests.map((test, index) => (
                          <div key={index} className="test-tag">
                            {test}
                            <button onClick={() => removeTest(index)}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SECTION: Plan */}
              <div id="section-plan" className="section-card section-card-plan">
                <div className="section-title">
                  <Calendar size={20} /> Advice & Plan
                </div>

                  <div className="form-group form-group-rel">
                    <label>Clinical Advice</label>
                    <div className="premium-input-wrapper">
                      <textarea 
                        value={advice} 
                        onChange={(e) => setAdvice(e.target.value)}
                        placeholder="Enter follow-up instructions..." rows="3" 
                      />
                      {isSpeechSupported && (
                        <button 
                          type="button" 
                          className={`mic-docked-btn ${listeningField === 'advice' ? 'active' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleListening('advice'); }}
                          data-tooltip={listeningField === 'advice' ? "Stop Recording" : "Speak to enter clinical advice"}
                        >
                          {listeningField === 'advice' ? <div className="stop-square" /> : <Mic size={18} />}
                        </button>
                      )}
                      <AnimatePresence>
                        {listeningField === 'advice' && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="dictation-status-floating"
                          >
                            <span className="dot pulse"></span> Listening...
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                <div className="form-group">
                  <label>Follow-up Date</label>
                  <div className="follow-up-container">
                    <input type="date" value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="follow-up-date-input"
                    />
                    <div className="follow-up-presets">
                      {[3, 7, 10, 14, 21, 30].map(days => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setFollowUpDuration(days)}
                          className="btn-icon-sm"
                        >
                          {days >= 30 ? '1m' : `${days}d`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER ACTIONS */}
              <div className="prescription-actions actions-footer-clean">
                <button
                  type="button"
                  onClick={() => handleSavePrescription('done')}
                  className="btn-primary btn-complete-rx"
                  disabled={loading || savingTemplate}
                >
                  {loading ? 'Processing...' : (
                    <>
                      <CheckCircle size={20} />
                      {editPrescriptionData ? 'Update Prescription' : 'Complete Prescription'}
                    </>
                  )}
                </button>

                {currentPrescriptionId && !editPrescriptionData && (
                  <button
                    type="button"
                    onClick={handleSendToAdditionalDept}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Send to Remaining Dept
                  </button>
                )}

                {!editPrescriptionData && !currentPrescriptionId && !activePresetName && (
                  <div className="footer-right-area">
                    {showPresetInput && (
                      <div className="save-preset-popover">
                        <h4>Save as Preset</h4>
                        <p>Name this clinical protocol for reuse.</p>
                        <input
                          type="text"
                          value={presetNameInput}
                          onChange={e => setPresetNameInput(e.target.value)}
                          placeholder="e.g. Viral Fever"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
                        />
                        <div className="popover-actions">
                          <button type="button" className="popover-cancel" onClick={() => setShowPresetInput(false)}>Cancel</button>
                          <button type="button" className="popover-confirm" onClick={handleSaveTemplate}>Save</button>
                        </div>
                      </div>
                    )}
                    <button type="button" onClick={() => setShowPresetInput(true)}
                      className="btn-secondary btn-save-preset" disabled={loading}>
                      ★ Save as Preset
                    </button>
                  </div>
                )}
              </div>

            </main>

            {/* Sidebar for PMH & Progress */}
            <aside className="rx-preview-sidebar no-print">
              
              {/* Past Medical History Card (Only if returning patient with conditions) */}
              {existingPatient?.conditions && (
                <div className="rx-stepper-sticky-card pmh-sidebar-card" style={{ marginBottom: '1.25rem' }}>
                  <div className="rx-stepper-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="rx-stepper-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} /> Past Medical History
                    </span>
                    {!isEditingPMH ? (
                      <button 
                        className="pmh-edit-btn" 
                        onClick={() => setIsEditingPMH(true)}
                        title="Edit History"
                      >
                        <Pencil size={14} />
                      </button>
                    ) : (
                      <button 
                        className="pmh-close-btn" 
                        onClick={() => setIsEditingPMH(false)}
                        title="Close Edit"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className="pmh-sidebar-content">
                    {isEditingPMH ? (
                      <div className="pmh-edit-mode">
                        <textarea
                          value={pastMedicalHistory}
                          onChange={(e) => setPastMedicalHistory(e.target.value)}
                          rows="3"
                          className="pmh-sidebar-textarea"
                          placeholder="Update medical history..."
                        />
                        {isSpeechSupported && (
                          <button 
                            type="button" 
                            className={`mic-docked-btn pmh-sidebar-mic ${listeningField === 'pastMedicalHistory' ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleListening('pastMedicalHistory'); }}
                            title={listeningField === 'pastMedicalHistory' ? "Stop Recording" : "Dictate"}
                          >
                            {listeningField === 'pastMedicalHistory' ? <div className="stop-square" /> : <Mic size={14} />}
                          </button>
                        )}
                        <p className="pmh-edit-hint">Changes will be saved with this prescription.</p>
                      </div>
                    ) : (
                      <div className="pmh-view-mode">
                        {pastMedicalHistory ? (
                          <p className="pmh-text">{pastMedicalHistory}</p>
                        ) : (
                          <p className="pmh-empty">No history recorded.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Stepper Progress Sidebar */}
              <div className="rx-stepper-sticky-card">
                <div className="rx-stepper-header">
                  <span className="rx-stepper-title">Prescription Progress</span>
                </div>
                <div className="premium-stepper-container">
                  <div className="stepper-progress-track">
                    <div 
                      className="stepper-progress-fill" 
                      style={{
                        height: 
                          activeSection === 'section-clinical' ? '0%' :
                          activeSection === 'section-medications' ? '33%' :
                          activeSection === 'section-investigations' ? '66%' :
                          activeSection === 'section-plan' ? '100%' : '0%'
                      }} 
                    />
                  </div>
                  <div className="stepper-steps-list">
                    {[
                      {
                        id: 'section-clinical',
                        label: 'Vitals & Notes',
                        icon: <Activity size={18} />,
                        checkComplete: () => !!(patientForm.symptoms.trim() && diagnosis.trim())
                      },
                      {
                        id: 'section-medications',
                        label: 'Medications',
                        icon: <Pill size={18} />,
                        checkComplete: () => medicines.some(m => m.name.trim() !== '')
                      },
                      {
                        id: 'section-investigations',
                        label: 'Lab Investigations',
                        icon: <FlaskConical size={18} />,
                        checkComplete: () => tests.length > 0
                      },
                      {
                        id: 'section-plan',
                        label: 'Advice & Plan',
                        icon: <Calendar size={18} />,
                        checkComplete: () => !!(advice.trim() || followUpDate)
                      }
                    ].map((s) => {
                      const isCompleted = s.checkComplete();
                      const isActive = activeSection === s.id;
                      
                      return (
                        <div 
                          key={s.id} 
                          className={`stepper-step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                          onClick={() => scrollToSection(s.id)}
                        >
                          <div className="stepper-circle-icon">
                            {isCompleted ? <CheckCircle size={16} /> : s.icon}
                          </div>
                          <div className="stepper-right-column">
                            <span className="stepper-label">{s.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
        </div>
      )}

      {viewingHistoryRx && (
        <PrescriptionViewer
          prescription={viewingHistoryRx}
          mode="view"
          onClose={() => setViewingHistoryRx(null)}
        />
      )}

      {showLeaveModal && (
        <div className="custom-confirm-overlay no-print">
          <div className="custom-confirm-modal">
            <div className="confirm-modal-header">
              <AlertTriangle size={24} className="confirm-warning-icon" />
              <h3>Discard Prescription?</h3>
            </div>
            <div className="confirm-modal-body">
              <p>Your prescription draft is not complete yet. Are you sure you want to leave? Any unsaved changes will be lost.</p>
            </div>
            <div className="confirm-modal-footer">
              <button className="btn-confirm-cancel" onClick={() => setShowLeaveModal(false)}>
                Keep Editing
              </button>
              <button className="btn-confirm-discard" onClick={() => { setShowLeaveModal(false); resetForm(); }}>
                Discard & Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatePrescription;